import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn, compararTexto } from "@/lib/utils";

import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { Input } from "@/components/ui/input";
import {
  InfoComplField,
  aplicarPreDefinicoesUnicas,
  infoComplObrigatoriasEfetivas,
  infoComplObrigatoriasFaltando,
} from "@/components/InfoComplField";
import {
  categoriaExigeValor,
  niveisObrigatoriosDaCategoria,
  niveisObrigatoriosFaltando,
} from "@/lib/camposObrigatoriosCategoria";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Eye,
  ExternalLink,
  ImageOff,
  Layers,
  MapPin,
  Package,
  Plus,
  Printer,
  RefreshCw,
  Tag,
} from "lucide-react";

import { useCategorias } from "@/contexts/CategoriasContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressao } from "@/components/ImpressaoComponent";
import { LocalizacaoCombobox } from "@/components/LocalizacaoCombobox";
import {
  CAMPOS_EPC,
  InfoIndividualEmMassa,
  aplicarInfoIndividual,
  dataParaInput,
  valoresDaCategoria,
} from "@/components/InfoIndividualEmMassa";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { useSeriais } from "@/contexts/SeriaisContext";
import { useItem } from "@/contexts/ItemContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { AdminContext } from "@/contexts/AdminContext";
import { useRestricoesUsuario } from "@/hooks/useRestricoesUsuario";
import {
  filtrarArvoreLocalizacoes,
  filtrarCategorias,
} from "@/lib/restricoesUsuario";

import { type CategoriaItemResponse } from "@/types/categoria-item-response";
import type { EpcItemGerado } from "@/types/epc-item-gerado";
import type { ColumnDef } from "@tanstack/react-table";
import type { ImpressaoItem } from "@/types/impressao-item";
import { type NewCategoriaItem } from "@/types/new-categoria-item";

import {
  brDateToISO,
  categoriaComboValue,
  converterListaZplItemParaUrl,
  formatarData,
  sanitizarCodigoHex,
  verificarEpcsInvalidos,
  verificarVariaveisDeSubstituicaoEmInfosComplementares,
} from "@/utils/UtilFuncoes";
import { alert } from "@/contexts/AlertModalContext";
import { indicesInfoCompl } from "@/lib/infoCompl";

const novaCategoriaPadrao = (id_conta: string): NewCategoriaItem => ({
  descricao: "",
  ean: "",
  inf_compl_1: "",
  inf_compl_2: "",
  inf_compl_3: "",
  inf_compl_4: "",
  inf_compl_5: "",
  inf_compl_6: "",
  inf_compl_7: "",
  inf_compl_8: "",
  inf_compl_9: "",
  inf_compl_10: "",
  inf_compl_11: "",
  inf_compl_12: "",
  id_conta,
});

// Célula da coluna "Informação Individual" extraída em componente pra evitar
// hooks dentro do `cell:` (anti-pattern).
function InformacaoIndividualCell({
  row,
  setEpcs,
}: {
  row: { index: number; original: EpcItemGerado };
  setEpcs: React.Dispatch<React.SetStateAction<EpcItemGerado[]>>;
}) {
  const { t } = useTranslation();
  const { quantidade_bo, valor_bo, dt_validade_bo } = row.original;
  const [open, setOpen] = useState(false);
  const [quantidade, setQuantidade] = useState(quantidade_bo || "");
  const [valor, setValor] = useState(valor_bo ?? 0);
  const [validade, setValidade] = useState(dt_validade_bo || "");

  useEffect(() => {
    if (!open) {
      setQuantidade(quantidade_bo || "");
      setValor(valor_bo ?? 0);
      setValidade(dt_validade_bo || "");
    }
  }, [open, quantidade_bo, valor_bo, dt_validade_bo]);

  const temValores = !!quantidade || valor > 0 || !!validade;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={temValores ? "outline" : "ghost"} size="sm">
          {temValores ? (
            <div className="flex flex-wrap gap-1">
              {quantidade && (
                <Badge variant="secondary" className="font-normal">
                  {t("avulsa.qty")} {quantidade}
                </Badge>
              )}
              {valor > 0 && (
                <Badge variant="secondary" className="font-normal">
                  R$ {valor.toFixed(2)}
                </Badge>
              )}
              {validade && (
                <Badge variant="secondary" className="font-normal">
                  {t("avulsa.val")} {validade}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{t("avulsa.assign")}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("avulsa.quantity")}</Label>
            <Input
              autoComplete="off"
              type="number"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("avulsa.valueBrl")}</Label>
            <Input
              autoComplete="off"
              type="number"
              step="0.01"
              value={valor}
              onChange={(e) => {
                const v = e.target.value;
                setValor(v === "" ? 0 : Number(v));
              }}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("avulsa.validity")}</Label>
            <Input
              autoComplete="off"
              type="date"
              value={dataParaInput(validade)}
              onChange={(e) =>
                setValidade(formatarData(e.target.value, false))
              }
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEpcs((prev) =>
                prev.map((item, idx) =>
                  idx === row.index
                    ? {
                        ...item,
                        quantidade_bo: quantidade,
                        valor_bo: valor === 0 ? undefined : Number(valor),
                        dt_validade_bo: validade,
                      }
                    : item,
                ),
              );
              setOpen(false);
            }}
          >
            {t("avulsa.save")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Célula da coluna "Pré-visualização"
function PreviewCell({
  item,
  coletaZplPadrao,
}: {
  item: EpcItemGerado;
  coletaZplPadrao: () => any;
}) {
  const { t } = useTranslation();
  const itemZpl = coletaZplPadrao();
  if (itemZpl && itemZpl.zpl) {
    itemZpl.zpl = item.zpl_impressao_bo;
  }
  if (!itemZpl) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <ImageOff className="h-3.5 w-3.5" />
        {t("avulsa.zplNotConfigured")}
      </span>
    );
  }
  const url = converterListaZplItemParaUrl(itemZpl);
  return (
    <HoverCard openDelay={300} closeDelay={50}>
      <HoverCardTrigger asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
        >
          <Eye className="h-3.5 w-3.5" />
          {t("avulsa.preview")}
        </a>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-2">
        <img
          src={url}
          alt={t("avulsa.previewAlt")}
          className="w-full h-auto bg-white rounded"
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          {t("avulsa.openInNewTab")}
        </a>
      </HoverCardContent>
    </HoverCard>
  );
}

function ImpressaoAvulsa() {
  const { t } = useTranslation();
  const { criarCategorias } = useCategorias();
  const { contaConfig, numeroDeImpressoes } = useContaConfig();
  const { adicionarItensNaFila } = useImpressao();
  const { isLoading } = useGlobalLoader();
  const { gerarEpcs } = useEpc();
  const { localizacoes: todasLocalizacoes, coletaLocalizacoes } =
    useLocalizacoes();
  const { impressaoConfig, coletarBlocosEPC, coletaZplPadrao } =
    useImpressaoConfig();
  const { categorias: todasCategorias, coletaCategorias } = useCategorias();

  // Restrições do usuário configuradas no backoffice (Configurações >
  // Usuários): só as localizações e as categorias liberadas para ele entram
  // nesta tela. Sem restrição configurada, as listas são as de sempre.
  const restricoes = useRestricoesUsuario();
  const localizacoes = useMemo(
    () => filtrarArvoreLocalizacoes(todasLocalizacoes, restricoes),
    [todasLocalizacoes, restricoes],
  );
  const categorias = useMemo(
    () => filtrarCategorias(todasCategorias, restricoes),
    [todasCategorias, restricoes],
  );
  // Com categorias restritas, uma categoria criada aqui ficaria fora da lista
  // liberada: o botão de criar some.
  const podeCriarCategoria = restricoes.categorias.length === 0;
  const { coletaSeriais, adicionarMaisUmNosSeriais } = useSeriais();
  const { conferirTagsCadastradas } = useItem();
  const { requireAdmin } = useContext(AdminContext)!;

  const [comboCodigo, setComboCodigo] = useState(false);
  const [quantidade, setQuantidade] = useState(1);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<
    CategoriaItemResponse | undefined
  >(undefined);
  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  const opcoesNivel2 = useMemo(
    () =>
      localizacoes.find((n1) => n1._id === localizacaoSelecionada)?.children ??
      [],
    [localizacoes, localizacaoSelecionada],
  );
  const opcoesNivel3 = useMemo(
    () =>
      opcoesNivel2.find((n2) => n2._id === localizacaoSelecionada2)
        ?.children ?? [],
    [opcoesNivel2, localizacaoSelecionada2],
  );
  const opcoesNivel4 = useMemo(
    () =>
      opcoesNivel3.find((n3) => n3._id === localizacaoSelecionada3)
        ?.children ?? [],
    [opcoesNivel3, localizacaoSelecionada3],
  );
  const [epcs, setEpcs] = useState<EpcItemGerado[]>([]);
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const [mostarMaisInfos, setMostarMaisInfos] = useState(false);

  // Nova categoria em Dialog
  const [novaCategoriaOpen, setNovaCategoriaOpen] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState<NewCategoriaItem>(
    novaCategoriaPadrao(impressaoConfig?.id_conta || ""),
  );

  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    Array<{ epc: string; codigo: string }>
  >([]);
  const [gerouListaImpressao, setGerouListaImpressao] = useState(false);

  // ---- LocalStorage preferências (chaves consistentes) ----
  useEffect(() => {
    const mostrarMais = localStorage.getItem("mostrarMaisInfos");
    setMostarMaisInfos(mostrarMais === "true");
    const repetidos = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(repetidos === "true");
  }, []);

  // ---- Nova categoria ----
  const handleCriarCategoria = async () => {
    if (!novaCategoria.descricao || !novaCategoria.ean) {
      alert.error(t("avulsa.descCodeRequired"));
      return;
    }
    if (isLoading) return;

    const eanSanitizado = sanitizarCodigoHex(novaCategoria.ean);
    if (eanSanitizado !== novaCategoria.ean.toUpperCase()) {
      alert.warning(
        t("avulsa.codeConverted", {
          original: novaCategoria.ean,
          novo: eanSanitizado,
        }),
      );
    }

    try {
      const novaCategoriaCriada = await criarCategorias([
        {
          ...novaCategoria,
          ean: eanSanitizado,
          id_conta: impressaoConfig?.id_conta || "",
        },
      ]);
      if (novaCategoriaCriada && novaCategoriaCriada.length !== 0) {
        setCategoriaSelecionada(
          aplicarPreDefinicoesUnicas(impressaoConfig, novaCategoriaCriada[0]),
        );
        alert.success(t("avulsa.categoryCreated"));
      }
      setNovaCategoria(
        aplicarPreDefinicoesUnicas(
          impressaoConfig,
          novaCategoriaPadrao(impressaoConfig?.id_conta || ""),
        ),
      );
      setNovaCategoriaOpen(false);
      setComboCodigo(false);
      await coletaCategorias();
    } catch {
      alert.error(t("avulsa.categoryCreateError"));
    }
  };

  const abrirNovaCategoria = () => {
    setComboCodigo(false);
    setNovaCategoria(
      aplicarPreDefinicoesUnicas(
        impressaoConfig,
        novaCategoriaPadrao(impressaoConfig?.id_conta || ""),
      ),
    );
    setNovaCategoriaOpen(true);
  };

  // ---- Definir localização padrão ----
  useEffect(() => {
    if (impressaoConfig && localizacoes.length > 0) {
      const n1 = localizacoes.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao,
      );
      setLocalizacaoSelecionada(n1?._id || "");
      const n2 = n1?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_2,
      );
      setLocalizacaoSelecionada2(n2?._id || "");
      const n3 = n2?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_3,
      );
      setLocalizacaoSelecionada3(n3?._id || "");
      const n4 = n3?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_4,
      );
      setLocalizacaoSelecionada4(n4?._id || "");
    }
    if (gerouListaImpressao) setGerouListaImpressao(false);
  }, [impressaoConfig, localizacoes, gerouListaImpressao]);

  // ---- Gerar EPCs quando categoria/quantidade mudam ----
  useEffect(() => {
    if (categoriaSelecionada) {
      const localizacoesZpl: string[] = [
        localizacoes.find((loc) => loc._id === localizacaoSelecionada)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((loc) => loc._id === localizacaoSelecionada2)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((loc) => loc._id === localizacaoSelecionada3)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((n3) => n3._id === localizacaoSelecionada3)
          ?.children.find((loc) => loc._id === localizacaoSelecionada4)
          ?.descricao || "",
      ];
      const novos = gerarEpcs(
        categoriaSelecionada,
        quantidade,
        localizacoesZpl,
        coletarBlocosEPC(),
      );
      // Informação individual padrão da categoria vira sugestão de cada etiqueta.
      setEpcs(
        aplicarInfoIndividual(
          novos,
          valoresDaCategoria(categoriaSelecionada),
          CAMPOS_EPC,
          true,
        ),
      );
    }
  }, [quantidade, categoriaSelecionada]);

  // ---- Sincronizar dados ----
  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setCategoriaSelecionada(undefined);
    alert.success(t("avulsa.syncSuccess"));
  };

  // ---- Gerar lista de impressão ----
  const gerarListaParaImpressao = async () => {
    if (!categoriaSelecionada) {
      alert.error(t("avulsa.selectCategory"));
      return;
    }
    if (!impressaoConfig) {
      alert.error(t("avulsa.configNotFound"));
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(t("avulsa.informHardwareId"));
      return;
    }
    if (!localizacaoSelecionada) {
      alert.error(t("avulsa.selectLocation"));
      return;
    }

    // Obrigatórios da configuração de impressão + os da categoria
    // (back office > Categorias > Campos Obrigatórios do Item).
    const faltandoObrigatorios = infoComplObrigatoriasFaltando(
      impressaoConfig,
      categoriaSelecionada,
      categoriaSelecionada,
    );
    const niveisFaltando = niveisObrigatoriosFaltando(
      categoriaSelecionada,
      [
        localizacaoSelecionada,
        localizacaoSelecionada2,
        localizacaoSelecionada3,
        localizacaoSelecionada4,
      ],
      [
        localizacoes.length,
        opcoesNivel2.length,
        opcoesNivel3.length,
        opcoesNivel4.length,
      ],
    );
    // Valor 0 não é guardado na etiqueta (vira vazio), então exigir valor é
    // exigir um valor maior que zero em cada etiqueta.
    const faltaValor =
      categoriaExigeValor(categoriaSelecionada) &&
      epcs.some((epc) => !epc.valor_bo);
    if (
      faltandoObrigatorios.length > 0 ||
      niveisFaltando.length > 0 ||
      faltaValor
    ) {
      const nomes = [
        ...faltandoObrigatorios.map(
          (idx) =>
            (contaConfig as any)?.[`info_compl_${idx}`] ||
            `Info. Compl. ${idx}`,
        ),
        ...niveisFaltando.map((nivel) => `Nível ${nivel}`),
        ...(faltaValor ? ["Valor (informação individual)"] : []),
      ];
      alert.error(
        `Preencha os campos obrigatórios antes de imprimir: ${nomes.join(", ")}.`,
      );
      return;
    }

    const {
      inf_compl_1,
      inf_compl_2,
      inf_compl_3,
      inf_compl_4,
      inf_compl_5,
      inf_compl_6,
      inf_compl_7,
      inf_compl_8,
      inf_compl_9,
      inf_compl_10,
      inf_compl_11,
      inf_compl_12,
    } = categoriaSelecionada;

    const numero_de_impressoes = numeroDeImpressoes || 0;

    const listaParaImpressao: ImpressaoItem[] = epcs.map((epc, index) => ({
      _id: epc._id,
      inf_compl_1: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_1,
      ),
      inf_compl_2: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_2,
      ),
      inf_compl_3: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_3,
      ),
      inf_compl_4: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_4,
      ),
      inf_compl_5: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_5,
      ),
      inf_compl_6: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_6,
      ),
      inf_compl_7: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_7,
      ),
      inf_compl_8: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_8,
      ),
      inf_compl_9: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_9,
      ),
      inf_compl_10: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_10,
      ),
      inf_compl_11: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_11,
      ),
      inf_compl_12: verificarVariaveisDeSubstituicaoEmInfosComplementares(
        epc,
        inf_compl_12,
      ),
      tag: epc.epc,
      codigo: categoriaSelecionada.ean,
      descricao: categoriaSelecionada.descricao,
      zpl_impressao: epc.zpl_impressao_bo,
      id_categoria: categoriaSelecionada._id,
      id_conta: impressaoConfig.id_conta,
      id_usuario: impressaoConfig.id_usuario,
      id_localizacao1: localizacaoSelecionada,
      id_localizacao2: localizacaoSelecionada2,
      id_localizacao3: localizacaoSelecionada3,
      id_localizacao4: localizacaoSelecionada4,
      valor: epc.valor_bo || 0,
      quantidade: epc.quantidade_bo || "",
      dt_validade: brDateToISO(epc.dt_validade_bo) || "",
      n_impressao: (numero_de_impressoes + index + 1).toString(),
      id_externo: "",
      ultima_localizacao: {
        primeira_vez: true,
        verificar: contaConfig?.transferencia_loc_origem || false,
      },
    }));

    if (listaParaImpressao.length === 0) {
      alert.error(t("avulsa.noEpcGenerated"));
      return;
    }

    if (!permitirItensRepetidos) {
      const itensNaBase = await conferirTagsCadastradas(
        listaParaImpressao.map((item) => item.tag),
      );
      if (itensNaBase.length > 0) {
        const repetidosFormatados = listaParaImpressao
          .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
          .map((item) => ({ epc: item.tag, codigo: item.codigo }));
        setEpcRepetidosEncontrados(repetidosFormatados);
        setEpcRepetidosDialog(true);
        adicionarMaisUmNosSeriais(
          repetidosFormatados.map((item) => item.codigo),
        );
        return;
      }
    }

    const epcs_para_impressao = listaParaImpressao.map((item) => item.tag);
    if (verificarEpcsInvalidos(epcs_para_impressao)) {
      alert.error(t("avulsa.invalidEpcs"));
      return;
    }
    adicionarItensNaFila(
      listaParaImpressao,
      undefined,
      undefined,
      MODOS_IMPRESSAO.AVULSA,
    );
    setCategoriaSelecionada(undefined);
    setGerouListaImpressao(true);
  };

  // ---- Colunas ----
  const columns: ColumnDef<EpcItemGerado>[] = [
    {
      accessorKey: "epc",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.original.epc}</span>
      ),
    },
    {
      id: "info_adicional_item",
      header: t("avulsa.individualInfo"),
      cell: ({ row }) => (
        <InformacaoIndividualCell row={row} setEpcs={setEpcs} />
      ),
    },
    {
      id: "preview",
      header: t("avulsa.previewCol"),
      cell: ({ row }) => (
        <PreviewCell item={row.original} coletaZplPadrao={coletaZplPadrao} />
      ),
    },
  ];

  if (!impressaoConfig) return null;

  // Campos de informação complementar (10, ou 12 com destino iNOVAID)
  const obrigatoriosInfoCompl = new Set(
    infoComplObrigatoriasEfetivas(impressaoConfig, categoriaSelecionada),
  );
  const niveisObrigatorios = new Set(
    niveisObrigatoriosDaCategoria(categoriaSelecionada),
  );
  const camposInfoCompl: { idx: number; label: string; obrigatorio: boolean }[] =
    indicesInfoCompl(impressaoConfig).map((i) => ({
      idx: i,
      label:
        (contaConfig as any)?.[`info_compl_${i}`] ||
        t("avulsa.infoCompl", { n: i }),
      obrigatorio: obrigatoriosInfoCompl.has(i),
    }));

  const camposPrimarios = camposInfoCompl.slice(0, 4);
  const camposExtras = camposInfoCompl.slice(4);

  // Só exibimos na listagem categorias que tenham descrição E código (ean).
  // Categorias sem código/ean ou sem descrição não aparecem na seleção.
  const categoriasValidas = categorias
    .filter((c) => !!c.descricao?.trim() && !!c.ean?.trim())
    .sort((a, b) => compararTexto(a.descricao, b.descricao));
  const podeImprimir =
    !!categoriaSelecionada && !!localizacaoSelecionada && epcs.length > 0;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Impressão Avulsa
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Imprima etiquetas RFID individuais ou em lote a partir de uma
            categoria
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="impressao-avulsa-mostrar-mais"
              disabled={isLoading}
              checked={mostarMaisInfos}
              onCheckedChange={(checked) => {
                setMostarMaisInfos(checked);
                localStorage.setItem("mostrarMaisInfos", String(checked));
              }}
            />
            <Label
              htmlFor="impressao-avulsa-mostrar-mais"
              className="text-xs cursor-pointer"
            >
              Mais campos
            </Label>
          </div>

          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="impressao-avulsa-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label
              htmlFor="impressao-avulsa-repetidos"
              className="text-xs cursor-pointer"
            >
              {t("avulsa.allowRepeated")}
            </Label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("avulsa.sync")}</span>
          </Button>

          <Button
            size="sm"
            onClick={gerarListaParaImpressao}
            disabled={!podeImprimir || isLoading}
            title={
              !podeImprimir
                ? t("avulsa.selectCategoryLocationToPrint")
                : t("avulsa.printNLabels", { count: epcs.length })
            }
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">{t("avulsa.print")}</span>
            {podeImprimir && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
              >
                {epcs.length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* 1. Seleção de categoria */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium">
              {t("avulsa.categoryAndQuantity")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
            <div className="grid gap-2">
              <Label htmlFor="impressao-avulsa-codigo">
                {t("avulsa.category")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Popover open={comboCodigo} onOpenChange={setComboCodigo}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="flex-1 justify-between font-normal"
                    >
                      {categoriaSelecionada ? (
                        <span className="truncate">
                          <span className="font-medium">
                            {categoriaSelecionada.descricao}
                          </span>
                          <span className="text-muted-foreground ml-2">
                            {categoriaSelecionada.ean}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          Selecione uma categoria
                        </span>
                      )}
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="p-0 w-(--radix-popover-trigger-width)"
                  >
                    <Command className="rounded-lg">
                      <CommandInput placeholder="Buscar por descrição, código ou info. complementar..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="text-xs text-muted-foreground p-2">
                            Nenhuma categoria encontrada
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {categoriasValidas.map((categoria, index) => (
                            <CommandItem
                              key={index}
                              value={categoriaComboValue(categoria)}
                              onSelect={(currentValue) => {
                                const ean = currentValue.split("|-|")[0].trim();
                                const sel = categorias.find(
                                  (c) => c.ean === ean,
                                );
                                setCategoriaSelecionada(
                                  sel
                                    ? aplicarPreDefinicoesUnicas(
                                        impressaoConfig,
                                        sel,
                                      )
                                    : sel,
                                );
                                setComboCodigo(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  categoriaSelecionada?.ean === categoria.ean
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-medium truncate">
                                  {categoria.descricao}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                  {categoria.ean}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                      {podeCriarCategoria && (
                        <div className="border-t p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start"
                            onClick={abrirNovaCategoria}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Nova categoria
                          </Button>
                        </div>
                      )}
                    </Command>
                  </PopoverContent>
                </Popover>
                {podeCriarCategoria && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={abrirNovaCategoria}
                    title="Criar nova categoria"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="impressao-avulsa-quantidade">
                {t("avulsa.quantityLabel")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                autoComplete="off"
                min={1}
                value={quantidade}
                disabled={isLoading}
                id="impressao-avulsa-quantidade"
                type="number"
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setQuantidade(!isNaN(v) && v > 0 ? v : 1);
                }}
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {categoriaSelecionada ? (
        <>
          {/* 2. Localizações */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  2
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {t("avulsa.locations")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="grid gap-2">
                  <Label
                    htmlFor="loc-1"
                    className="text-xs text-muted-foreground"
                  >
                    Nível 1{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <LocalizacaoCombobox
                    id="loc-1"
                    disabled={isLoading}
                    opcoes={localizacoes}
                    value={localizacaoSelecionada}
                    onChange={(value) => {
                      setLocalizacaoSelecionada(value);
                      setLocalizacaoSelecionada2("");
                      setLocalizacaoSelecionada3("");
                      setLocalizacaoSelecionada4("");
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="loc-2"
                    className="text-xs text-muted-foreground"
                  >
                    {t("avulsa.level2")}
                    {niveisObrigatorios.has(2) && (
                      <span className="text-destructive ml-0.5">*</span>
                    )}
                  </Label>
                  <LocalizacaoCombobox
                    id="loc-2"
                    disabled={isLoading || !localizacaoSelecionada}
                    opcoes={opcoesNivel2}
                    value={localizacaoSelecionada2}
                    onChange={(v) => {
                      setLocalizacaoSelecionada2(v);
                      setLocalizacaoSelecionada3("");
                      setLocalizacaoSelecionada4("");
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="loc-3"
                    className="text-xs text-muted-foreground"
                  >
                    {t("avulsa.level3")}
                    {niveisObrigatorios.has(3) && (
                      <span className="text-destructive ml-0.5">*</span>
                    )}
                  </Label>
                  <LocalizacaoCombobox
                    id="loc-3"
                    disabled={
                      isLoading ||
                      !localizacaoSelecionada ||
                      !localizacaoSelecionada2
                    }
                    opcoes={opcoesNivel3}
                    value={localizacaoSelecionada3}
                    onChange={(v) => {
                      setLocalizacaoSelecionada3(v);
                      setLocalizacaoSelecionada4("");
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="loc-4"
                    className="text-xs text-muted-foreground"
                  >
                    {t("avulsa.level4")}
                    {niveisObrigatorios.has(4) && (
                      <span className="text-destructive ml-0.5">*</span>
                    )}
                  </Label>
                  <LocalizacaoCombobox
                    id="loc-4"
                    disabled={
                      isLoading ||
                      !localizacaoSelecionada ||
                      !localizacaoSelecionada2 ||
                      !localizacaoSelecionada3
                    }
                    opcoes={opcoesNivel4}
                    value={localizacaoSelecionada4}
                    onChange={setLocalizacaoSelecionada4}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. Dados do item */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  3
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  Dados do item
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2">
                <Label htmlFor="impressao-avulsa-descricao">
                  Descrição <span className="text-destructive">*</span>
                </Label>
                <Input
                  autoComplete="off"
                  disabled={isLoading}
                  id="impressao-avulsa-descricao"
                  type="text"
                  value={categoriaSelecionada?.descricao || ""}
                  onChange={(e) =>
                    setCategoriaSelecionada({
                      ...categoriaSelecionada!,
                      descricao: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {camposPrimarios.map((c) => (
                  <div key={c.idx} className="grid gap-2">
                    <Label
                      htmlFor={`impressao-avulsa-info-${c.idx}`}
                      className="text-xs text-muted-foreground"
                    >
                      {c.label}
                      {c.obrigatorio && (
                        <span className="text-destructive ml-0.5">*</span>
                      )}
                    </Label>
                    <InfoComplField
                      idx={c.idx}
                      disabled={isLoading}
                      id={`impressao-avulsa-info-${c.idx}`}
                      value={
                        ((categoriaSelecionada as any)?.[
                          `inf_compl_${c.idx}`
                        ] as string) || ""
                      }
                      onChange={(v) =>
                        setCategoriaSelecionada({
                          ...categoriaSelecionada!,
                          [`inf_compl_${c.idx}`]: v,
                        } as CategoriaItemResponse)
                      }
                    />
                  </div>
                ))}
              </div>

              {mostarMaisInfos && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t">
                  {camposExtras.map((c) => (
                    <div key={c.idx} className="grid gap-2">
                      <Label
                        htmlFor={`impressao-avulsa-info-${c.idx}`}
                        className="text-xs text-muted-foreground"
                      >
                        {c.label}
                        {c.obrigatorio && (
                          <span className="text-destructive ml-0.5">*</span>
                        )}
                      </Label>
                      <InfoComplField
                        idx={c.idx}
                        disabled={isLoading}
                        id={`impressao-avulsa-info-${c.idx}`}
                        value={
                          ((categoriaSelecionada as any)?.[
                            `inf_compl_${c.idx}`
                          ] as string) || ""
                        }
                        onChange={(v) =>
                          setCategoriaSelecionada({
                            ...categoriaSelecionada!,
                            [`inf_compl_${c.idx}`]: v,
                          } as CategoriaItemResponse)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 4. Informação individual em massa */}
          <InfoIndividualEmMassa
            numero={4}
            totalItens={epcs.length}
            disabled={isLoading}
            valoresIniciais={valoresDaCategoria(categoriaSelecionada)}
            onAplicar={(valores) =>
              setEpcs((prev) => aplicarInfoIndividual(prev, valores, CAMPOS_EPC))
            }
          />

          {/* 5. Etiquetas geradas */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    5
                  </div>
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    Etiquetas geradas
                  </CardTitle>
                </div>
                <Badge variant="secondary" className="font-medium">
                  {epcs.length}{" "}
                  {epcs.length === 1 ? "etiqueta" : "etiquetas"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable columns={columns} data={epcs} />
            </CardContent>
          </Card>

        </>
      ) : (
        // Empty state: nenhuma categoria selecionada
        <Card>
          <CardContent className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Tag className="h-6 w-6" />
            </div>
            <div className="text-center max-w-sm">
              <p className="text-sm font-medium text-foreground">
                Selecione uma categoria para começar
              </p>
              <p className="text-xs">
                Escolha uma categoria existente acima ou crie uma nova.
                Os campos do item e a tabela de etiquetas aparecerão aqui.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: nova categoria */}
      <Dialog open={novaCategoriaOpen} onOpenChange={setNovaCategoriaOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("avulsa.newCategoryDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("avulsa.newCategoryDialogDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-2">
            <div className="grid gap-2">
              <Label htmlFor="nova-cat-descricao">
                {t("avulsa.descRequired")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                autoComplete="off"
                id="nova-cat-descricao"
                value={novaCategoria.descricao}
                onChange={(e) =>
                  setNovaCategoria({
                    ...novaCategoria,
                    descricao: e.target.value,
                  })
                }
                placeholder="Ex: Camiseta P Branca"
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="nova-cat-ean">
                {t("avulsa.codeRequired")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                autoComplete="off"
                id="nova-cat-ean"
                value={novaCategoria.ean}
                onChange={(e) =>
                  setNovaCategoria({ ...novaCategoria, ean: e.target.value })
                }
                placeholder="EAN ou código hexadecimal"
                className=""
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="grid gap-2">
                  <Label
                    htmlFor={`nova-cat-info-${i}`}
                    className="text-xs text-muted-foreground"
                  >
                    {(contaConfig as any)?.[`info_compl_${i}`] ||
                      t("avulsa.infoCompl", { n: i })}
                  </Label>
                  <InfoComplField
                    idx={i}
                    id={`nova-cat-info-${i}`}
                    value={
                      ((novaCategoria as any)[`inf_compl_${i}`] as string) || ""
                    }
                    onChange={(v) =>
                      setNovaCategoria({
                        ...novaCategoria,
                        [`inf_compl_${i}`]: v,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNovaCategoriaOpen(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleCriarCategoria}
              disabled={isLoading}
            >
              <Plus className="h-4 w-4" />
              Criar categoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>{t("avulsa.repeatedEpcsFound")}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {t("avulsa.repeatedEpcsDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary" className="">
                    {item.codigo}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("common2.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              {t("common2.understood")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImpressaoAvulsa;
